import moment from 'moment';

import { PayPalPayoutDisbursementCompositeKeyPrefix, PayoutNoteDelimiters } from 'shared/types';

const HeaderItem = {
  TransactionID: 'Transaction ID',
  Recipient: 'Recipient',
  RecipientName: 'Recipient Name',
  UniqueIdentifier: 'Unique Identifier',
  Total: 'Total',
  TotalUSD: 'Total (USD)',
  Status: 'Status',
  CustomNote: 'Custom Note',
};
const DI_PREFIX_SEPARATOR_CHARACTER = '-';
const DI_PIPE_SEPARATOR_CHARACTER = '|';

const extractPayoutNoteFromCustomNote = (str: string): string => {
  const [, , , , payoutNote] = str.split(DI_PIPE_SEPARATOR_CHARACTER);

  return payoutNote;
};

const isProcessingFeeLine = (customNote: string) =>
  customNote && customNote.includes(PayoutNoteDelimiters.ProcessingFee);
const isFundsTransfer = (customNote: string) =>
  customNote && customNote.includes(PayoutNoteDelimiters.GAWFundsTransfer);
const isCoveredFeeLine = (payoutNote: string) =>
  payoutNote && payoutNote.startsWith(PayoutNoteDelimiters.CoveredFee);

const isGAWRelated = (customNote: string) =>
  isProcessingFeeLine(customNote) || isFundsTransfer(customNote);

const isGAWForCoveredFeesRelated = (payoutNote: string) => isCoveredFeeLine(payoutNote);

const isMatchLine = (payoutNote: string) =>
  payoutNote && payoutNote.startsWith(PayoutNoteDelimiters.Match);

const isCorporateDonationLine = (payoutNote: string) =>
  payoutNote && payoutNote.startsWith(PayoutNoteDelimiters.Corporate);

const getBatchTypePrefix = (payoutNote: string) => {
  if (isMatchLine(payoutNote)) {
    return `${PayPalPayoutDisbursementCompositeKeyPrefix.Match}${DI_PREFIX_SEPARATOR_CHARACTER}`;
  } else if (isCorporateDonationLine(payoutNote)) {
    return `${PayPalPayoutDisbursementCompositeKeyPrefix.Donation}${DI_PREFIX_SEPARATOR_CHARACTER}`;
  }

  return '';
};

const hasExistingDIPrefix = (str: string) =>
  [
    PayPalPayoutDisbursementCompositeKeyPrefix.Donation,
    PayPalPayoutDisbursementCompositeKeyPrefix.Match,
  ].some((prefix) => str.startsWith(`${prefix}${DI_PREFIX_SEPARATOR_CHARACTER}`));

const processedAtDataForPPGFCSV = (dataField: unknown[]): string => {
  const data = Object.values(dataField) as string[];
  const potentialDataField = data[0];
  if (!data || !potentialDataField) {
    return '';
  }
  if (potentialDataField.toString().indexOf('Date and time of completed:') > -1) {
    let dateStr = potentialDataField
      .substr('Date and time of completed:'.length, potentialDataField?.length)
      .trim();
    dateStr = dateStr.replace(' at', ',');
    return moment(dateStr).format();
  }
  return '';
};

const extractCurrency = (amountStr: string, prefix: string, suffix: string) =>
  Number(amountStr.replace(prefix, '').replace(suffix, '').toString().trim());

const extractIndexesOfEssential = (dataRow: string[]) => {
  const headerIndexes: Record<string, number> = {};
  const headerLiterals = Object.values(HeaderItem);

  for (let i = 0; i < dataRow.length; i += 1) {
    if (headerLiterals.includes(dataRow[i])) {
      headerIndexes[dataRow[i]] = i;
    }
  }
  return headerIndexes;
};

export const extractPPGFCSVData = (data: unknown[]) => {
  let headerFound = false;
  const finalData = { processedAt: '', data: [] as unknown[] };
  // Once we see this, then the next rows should contain the records
  const literalStartingDataLine = [HeaderItem.TransactionID, HeaderItem.Recipient];
  // USD files don't contain `exchange rate`
  const nonUSDIndicator = 'exchange rate';
  let headerIndexes: Record<string, number> = {};
  // These values are constant characteristics of the PPGF file based on the agreed schema.
  let isNonUSDFile = false;

  for (let index = 0; index < data.length; index += 1) {
    const item = data[index] as string[];
    if (!finalData.processedAt || finalData.processedAt.trim().length === 0) {
      finalData.processedAt = processedAtDataForPPGFCSV(item as unknown[]);
      // eslint-disable-next-line no-continue
      continue;
    }

    if (item[0] && typeof item[0] !== 'string') {
      // NOTe-RA: we need to cast the 1st column to a string because
      // sometimes these values get parsed as numbers and then they cause logical
      // bugs because of how we later process them in code
      item[0] = String(item[0]);
    }

    if (item[0] && item[0].length > 0) {
      if (!headerFound) {
        if (!isNonUSDFile && item[0].toLowerCase().includes(nonUSDIndicator)) {
          isNonUSDFile = true;
        }
        if (
          item[0].includes(literalStartingDataLine[0]) &&
          item[1].includes(literalStartingDataLine[1])
        ) {
          // Records should follow this row
          headerFound = true;
          // Since the header is found already, then we can extract essential Header indexes
          headerIndexes = extractIndexesOfEssential(item);
          // Check that the header indexes have been set previously
          if (Object.keys(headerIndexes).length <= 0) {
            const errorMsg =
              'File headers not detected. This suggests this file does not match the expected format';
            throw new Error(errorMsg);
          }
        }
      } else {
        const customNote = item[headerIndexes[HeaderItem.CustomNote]];
        const uniqueIdentifier = item[headerIndexes[HeaderItem.UniqueIdentifier]];
        let disbursementBatchTypePrefix = '';

        const payoutNote = extractPayoutNoteFromCustomNote(customNote);

        if (!isGAWRelated(customNote) && !hasExistingDIPrefix(uniqueIdentifier)) {
          disbursementBatchTypePrefix = getBatchTypePrefix(payoutNote);
        }

        const prefixedUniqueIdentifier = `${disbursementBatchTypePrefix}${uniqueIdentifier}`;

        finalData.data.push({
          transactionId: item[headerIndexes[HeaderItem.TransactionID]],
          recipient: item[headerIndexes[HeaderItem.Recipient]],
          recipientName: item[headerIndexes[HeaderItem.RecipientName]],
          uniqueIdentifier: prefixedUniqueIdentifier,
          totalAmountCurrency: extractCurrency(
            isNonUSDFile
              ? item[headerIndexes[HeaderItem.TotalUSD]]
              : item[headerIndexes[HeaderItem.Total]],
            '$',
            'USD'
          ),
          givingAccountWithdrawal: isGAWRelated(customNote) ? uniqueIdentifier : null,
          status: item[headerIndexes[HeaderItem.Status]],
          givingAccountWithdrawalForCoveredFees: isGAWForCoveredFeesRelated(payoutNote)
            ? uniqueIdentifier
            : null,
        });
      }
    }
  }
  return finalData;
};
